import { FC, useCallback } from "react";
import { UserData, UserStatus } from "@/entities/users/types";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { simpleDate } from "@/utils/datetime";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";

const TRANSLATION_KEY = "settings.userManagement";

type UserStatusColorMap = {
  [K in UserStatus]: string;
} & { null: string };

export const userStatusColors: UserStatusColorMap = {
  [UserStatus.ACTIVE]: "var(--cta)",
  [UserStatus.INACTIVE]: "var(--error)",
  [UserStatus.PENDING]: "var(--warning)",
  [UserStatus.EXPIRED]: "var(--error)",
  null: "var(--border-field-default)"
};

export const UserTitleCell: FC<{ userData: UserData }> = ({ userData }) => {
  const { t } = useTranslation();

  if (!userData.jobTitle && !userData.departmentName) {
    return (
      <Text variant="bodyTertiary">
        {t(`${TRANSLATION_KEY}.placeholders.noData`)}
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Text variant={userData.jobTitle ? "body" : "bodyTertiary"} className={userData.jobTitle ? "font-medium" : ""}>
        {userData.jobTitle || t(`${TRANSLATION_KEY}.placeholders.noData`)}
      </Text>
      {userData.departmentName && (
        <Text variant="smBodySecondary">
          {userData.departmentName}
        </Text>
      )}
    </div>
  );
};

export const UserRoleBadge: FC<{ role: string }> = ({ role }) => {
  const { t } = useTranslation();

  if (!role) {
    return (
      <Text variant="bodyTertiary">
        {t(`${TRANSLATION_KEY}.placeholders.noData`)}
      </Text>
    );
  }

  return (
    <Badge text={role} textVariant="smBody" className="rounded-md py-1 px-2" />
  );
};

export const UserActionMenu: FC<{
  userData: UserData;
  onDeactivate: (userData: UserData) => void;
  onActivate: (userData: UserData) => void;
  onEdit: (userData: UserData) => void;
  isManagedByIdentityProvider?: boolean;
}> = ({
  userData,
  onDeactivate,
  onActivate,
  onEdit,
  isManagedByIdentityProvider = false
}) => {
  const userActions = {
    edit: () => onEdit(userData),
    ...(isManagedByIdentityProvider
      ? {}
      : userData.status === UserStatus.INACTIVE
      ? {
          activate: {
            action: () => onActivate(userData),
            variant: "default" as const
          }
        }
      : {
          deactivate: {
            action: () => onDeactivate(userData),
            variant: "destructive" as const
          }
        })
  };

  return (
    <OptionsDropdownMenu
      options={userActions}
      tPrefix={`${TRANSLATION_KEY}.actions`}
      buttonVariant="iconNoBg"
      iconColor="secondary"
      showIcons={false}
    />
  );
};

export const useUserTableHelpers = () => {
  const { t } = useTranslation();

  const getManagerName = useCallback(
    (managerId?: string, users?: UserData[]) => {
      if (!managerId || !users)
        return t(`${TRANSLATION_KEY}.placeholders.noData`);
      const manager = users.find((user) => user.id === managerId);
      return (
        manager?.displayName || t(`${TRANSLATION_KEY}.placeholders.noData`)
      );
    },
    [t]
  );

  const formatDate = useCallback(
    (dateString: string) => {
      if (!dateString) return t(`${TRANSLATION_KEY}.placeholders.noData`);
      try {
        return simpleDate(new Date(dateString), "long");
      } catch {
        return t(`${TRANSLATION_KEY}.placeholders.noData`);
      }
    },
    [t]
  );

  return {
    getManagerName,
    formatDate
  };
};
